import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Check, ChevronLeft, ChevronRight, Fuel, Gauge, Settings as Gear, Users, MapPin } from "lucide-react";
import { cloudinaryThumb, dkk, plain } from "../../lib/publicUtils";

function formatDanishDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("da-DK", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicDetail({ type = "rent" }) {
  const { plate } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/cars/${plate}`)
      .then((r) => setCar(r.data))
      .catch(() => setCar(null))
      .finally(() => setLoading(false));
  }, [plate]);

  const photos = car?.public_photos?.length ? car.public_photos : null;
  const hasMultiple = (photos?.length || 0) > 1;

  // Keyboard arrows — must be defined before any conditional return (React rules)
  useEffect(() => {
    if (!hasMultiple) return undefined;
    function onKey(e) {
      if (e.key === "ArrowLeft") setActivePhoto((i) => (i - 1 + photos.length) % photos.length);
      else if (e.key === "ArrowRight") setActivePhoto((i) => (i + 1) % photos.length);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hasMultiple, photos]);

  if (loading) return <div className="py-32 text-center text-graphite/50">Indlæser…</div>;
  if (!car) {
    return (
      <div className="py-32 text-center max-w-md mx-auto" data-testid="detail-not-found">
        <h2 className="font-heading text-3xl text-graphite mb-3">Bilen findes ikke</h2>
        <p className="text-graphite/60 mb-6">Den er måske udlejet eller solgt. Kig på de andre biler.</p>
        <Link to={type === "rent" ? "/biludlejning" : "/til-salg"} className="btn-outline">
          ← Tilbage til oversigt
        </Link>
      </div>
    );
  }

  const isRent = type === "rent";
  const backLink = isRent ? "/biludlejning" : "/til-salg";
  const backLabel = isRent ? "Tilbage til biludlejning" : "Tilbage til biler til salg";
  const hasPhotos = !!photos && photos.length > 0;
  const mainImg = hasPhotos
    ? cloudinaryThumb(photos[activePhoto].url, "c_fill,w_1600,q_auto,f_auto")
    : null;

  function nav(dir) {
    if (!photos) return;
    setActivePhoto((i) => (i + dir + photos.length) % photos.length);
  }

  return (
    <div className="bg-[#F7F3EC] min-h-screen pb-24" data-testid={`detail-${plate}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <Link to={backLink} className="inline-flex items-center gap-2 text-graphite/70 hover:text-graphite text-sm mb-6" data-testid="detail-back">
          <ArrowLeft size={16} /> {backLabel}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#F1EBE1] mb-3 relative">
              {isRent && !car.is_gomore_public && car.is_rented_out && (
                <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2">
                  <span className="bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg" data-testid="detail-gallery-rented">
                    ● Udlejet
                  </span>
                  {car.rental_return_date && (() => {
                    const [y, m, d] = car.rental_return_date.split("-");
                    return <span className="bg-white/95 text-graphite px-3 py-1.5 rounded-full text-xs font-semibold shadow-md">Retur {d}.{m}.{y}</span>;
                  })()}
                </div>
              )}
              {isRent && !car.is_gomore_public && !car.is_rented_out && (
                <div className="absolute top-4 left-4 z-10">
                  <span className="bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg inline-flex items-center gap-1.5" data-testid="detail-gallery-available">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> Ledig
                  </span>
                </div>
              )}
              {hasPhotos ? (
                <img src={mainImg} alt={`${car.make} ${car.model}`} className={`w-full h-full object-cover ${isRent && !car.is_gomore_public && car.is_rented_out ? "grayscale-[30%]" : ""}`} />
              ) : (
                <div className="w-full h-full flex items-center justify-center" data-testid="detail-no-photos">
                  <div className="text-center px-4">
                    <div className="text-xs uppercase tracking-[0.25em] text-graphite/40 mb-2">{car.make} {car.model}</div>
                    <div className="font-heading text-2xl sm:text-3xl text-graphite/65">Billeder på vej</div>
                  </div>
                </div>
              )}
            </div>
            {photos && photos.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {photos.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePhoto(i)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      i === activePhoto ? "border-copper" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                    data-testid={`thumb-${i}`}
                  >
                    <img src={cloudinaryThumb(p.url, "c_fill,w_200,q_auto,f_auto")} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info + form */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {car.category && <div className="label-tag">{car.category}</div>}
              {isRent && car.status === "lager" && (
                <span className="bg-copper text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid="detail-coming-badge">
                  Snart tilgængelig
                </span>
              )}
              {isRent && car.status !== "lager" && car.is_gomore_public && (
                <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid="detail-gomore-badge">
                  Dags leje
                </span>
              )}
              {isRent && car.status !== "lager" && !car.is_gomore_public && (
                <span className="bg-graphite text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid="detail-longterm-badge">
                  Langtid leje
                </span>
              )}
              {isRent && car.status !== "lager" && !car.is_gomore_public && car.is_rented_out && (
                <span className="bg-red-600 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="detail-rented-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"/> Udlejet
                </span>
              )}
              {isRent && car.status !== "lager" && !car.is_gomore_public && !car.is_rented_out && (
                <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="detail-available-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> Ledig
                </span>
              )}
            </div>
            <h1 className="font-heading text-4xl md:text-5xl text-graphite mb-1 leading-tight">{car.make} {car.model}</h1>
            {car.variant && <p className="text-graphite/60 mb-2">{car.variant}</p>}
            {isRent && car.public_location && (
              <p className="text-graphite/70 mb-3 inline-flex items-center gap-1.5 text-sm" data-testid="detail-location">
                <MapPin size={14} strokeWidth={1.7}/> {car.public_location}
              </p>
            )}
            {isRent && !car.is_gomore_public && car.is_rented_out && car.rental_return_date && (() => {
              const [y, m, d] = car.rental_return_date.split("-");
              return (
                <div className="mb-5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="detail-rented-banner">
                  <Calendar size={16} className="text-red-600 mt-0.5 shrink-0"/>
                  <div>
                    <div className="text-sm font-semibold text-red-700">Bilen er udlejet lige nu</div>
                    <div className="text-sm text-graphite/75">Forventet retur: <strong>{d}.{m}.{y}</strong> — skriv jer på venteliste nedenfor.</div>
                  </div>
                </div>
              );
            })()}
            {!((isRent && car.public_location) || (isRent && !car.is_gomore_public && car.is_rented_out)) && <div className="mb-5" />}

            {/* Specs */}
            <div className="grid grid-cols-2 gap-3 mb-8 text-sm">
              {car.year && <SpecRow label="Årgang" value={car.year} />}
              {car.first_registration_date && <SpecRow label="1. registrering" value={formatDanishDate(car.first_registration_date)} />}
              {Number.isFinite(car.current_km) && <SpecRow label="Kilometer" value={`${plain(car.current_km)} km`} icon={<Gauge size={14} strokeWidth={1.5}/>} />}
              {car.fuel_type && <SpecRow label="Brændstof" value={car.fuel_type} icon={<Fuel size={14} strokeWidth={1.5}/>} />}
              {car.transmission && <SpecRow label="Gearkasse" value={car.transmission} icon={<Gear size={14} strokeWidth={1.5}/>} />}
              {car.seats && <SpecRow label="Sæder" value={car.seats} icon={<Users size={14} strokeWidth={1.5}/>} />}
              {car.doors && <SpecRow label="Døre" value={car.doors} />}
              {!isRent && car.vin && <SpecRow label="Stelnummer" value={car.vin} mono />}
            </div>

            {/* Prices */}
            {isRent ? (
              car.is_gomore_public && car.gomore_link ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6" data-testid="gomore-pricing-notice">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-sm shrink-0">
                      G
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-graphite mb-1">Tilgængelig på GoMore</h3>
                      <p className="text-sm text-graphite/70 leading-relaxed">
                        Denne bil udlejes via GoMore. Klik nedenfor for at se aktuelle priser, ledige perioder og booke direkte hos GoMore.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-6 mb-6 border border-[#E5DED1]">
                  <div className="label-tag mb-4">Bilabonnement · vælg binding</div>
                  {(() => {
                  const opts = [
                    { months: 1, price: car.sub_price_1m, label: "1 måned" },
                    { months: 3, price: car.sub_price_3m, label: "3 måneder" },
                    { months: 6, price: car.sub_price_6m, label: "6 måneder" },
                    { months: 12, price: car.sub_price_12m, label: "12 måneder" },
                  ];
                  const valid = opts.filter((o) => o.price && o.price > 0);
                  if (valid.length === 0) {
                    return <div className="text-graphite/70 text-sm">Pris ved henvendelse — kontakt os for at høre nærmere.</div>;
                  }
                  const minPrice = Math.min(...valid.map((o) => o.price));
                  const useGoMore = !!(car.is_gomore_public && car.gomore_link);
                  return (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="subscription-grid">
                        {opts.map((o) => {
                          const enabled = o.price && o.price > 0;
                          const isBest = enabled && o.price === minPrice && valid.length > 1;
                          let CardEl, cardProps;
                          if (!enabled) {
                            CardEl = "div"; cardProps = {};
                          } else if (useGoMore) {
                            CardEl = "a";
                            cardProps = { href: car.gomore_link, target: "_blank", rel: "noopener noreferrer" };
                          } else {
                            CardEl = Link;
                            cardProps = { to: `/biludlejning/${car.license_plate}/book?binding=${o.months}` };
                          }
                          return (
                            <CardEl
                              key={o.months}
                              {...cardProps}
                              className={`relative rounded-xl border-2 p-3 text-center transition-all block ${
                                enabled ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5" : "opacity-60"
                              } ${
                                isBest ? "border-copper bg-copper/5" : enabled ? "border-[#E5DED1] bg-white hover:border-graphite/40" : "border-[#F1EBE1] bg-[#FAF6EE]"
                              }`}
                              data-testid={`sub-${o.months}m`}
                            >
                              {isBest && (
                                <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-copper text-white text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
                                  Bedste pris
                                </span>
                              )}
                              <div className="text-[10px] uppercase tracking-wider text-graphite/55 mb-1">{o.label}</div>
                              <div className={`font-heading ${isBest ? "text-2xl text-graphite" : "text-xl text-graphite/85"}`}>
                                {enabled ? dkk(o.price) : "—"}
                              </div>
                              <div className="text-[9px] uppercase tracking-wider text-graphite/45 mt-0.5">pr. md.</div>
                              {enabled && (
                                <div className="text-[10px] text-copper font-semibold mt-1.5 group-hover:underline">{useGoMore ? "GoMore →" : "Book →"}</div>
                              )}
                            </CardEl>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-graphite/55 mt-4 leading-relaxed">
                        Vælg den bindingsperiode der passer dig bedst — jo længere, jo lavere månedspris. Forsikring og vejhjælp kan tilkøbes.
                      </p>
                    </>
                  );
                })()}
              </div>
              )
            ) : (
              <div className="bg-white rounded-2xl p-6 mb-6 border border-[#E5DED1]">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="label-tag">Salgspris</div>
                  {car.sale_type === "engros" && (
                    <span className="bg-graphite text-copper px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider" data-testid="detail-sale-type">Engros</span>
                  )}
                  {car.sale_type === "formidling" && (
                    <span className="bg-copper/15 text-copper-dark px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider" data-testid="detail-sale-type">Formidling</span>
                  )}
                </div>
                {car.sale_price
                  ? <div className="font-heading text-4xl text-graphite">{dkk(car.sale_price)}</div>
                  : <div className="font-heading text-2xl text-graphite/80">Pris ved henvendelse</div>}
              </div>
            )}

            {/* Features */}
            {car.public_features?.length > 0 && (
              <div className="mb-8">
                <div className="label-tag mb-3 label-tag-dark">Udstyr</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {car.public_features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-graphite/80">
                      <Check size={14} className="text-copper" strokeWidth={2}/>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Description — vises kun ved bilsalg, ikke ved biludlejning */}
            {!isRent && car.public_description && (
              <div className="mb-8">
                <div className="label-tag mb-3 label-tag-dark">Beskrivelse</div>
                <p className="text-graphite/75 leading-relaxed whitespace-pre-line">{car.public_description}</p>
              </div>
            )}

            {isRent ? (
              car.is_gomore_public && car.gomore_link ? (
                <a
                  href={car.gomore_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full block text-center"
                  style={{ background: "#10B981" }}
                  data-testid="book-cta-gomore"
                >
                  Book på GoMore →
                </a>
              ) : (
                <Link to={`/biludlejning/${car.license_plate}/book`} className="btn-primary w-full block text-center" data-testid="book-cta">
                  Book denne bil →
                </Link>
              )
            ) : (
              <BookingForm car={car} type="sale_inquiry" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecRow({ label, value, icon, mono }) {
  return (
    <div className="bg-white rounded-xl px-4 py-3 border border-[#E5DED1]">
      <div className="text-[10px] uppercase tracking-wider text-graphite/50 mb-1 flex items-center gap-1">{icon}{label}</div>
      <div className={`font-medium text-graphite ${mono ? "font-mono text-xs break-all" : ""}`}>{value}</div>
    </div>
  );
}

function PriceBox({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-graphite/50 mb-1">{label}</div>
      <div className={`font-heading ${highlight ? "text-3xl text-graphite" : "text-xl text-graphite/70"}`}>{dkk(value)}</div>
    </div>
  );
}

function BookingForm({ car, type }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", start_date: "", binding_months: "", deductible: "15000", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const isRental = type === "rental";

  // Selvrisiko-tilvalg
  const DEDUCTIBLE_OPTS = [
    { value: "15000", label: "15.000 kr.", addon: 0, hint: "Standard — ingen ekstra" },
    { value: "5000", label: "5.000 kr.", addon: 500, hint: "Reducer for +500 kr./md" },
    { value: "0", label: "0 kr.", addon: 900, hint: "Ingen selvrisiko for +900 kr./md" },
  ];
  const deductibleSel = DEDUCTIBLE_OPTS.find((d) => d.value === form.deductible) || DEDUCTIBLE_OPTS[0];

  // Bilabonnement-options for denne bil
  const subOptions = isRental
    ? [
        { months: 1, price: car.sub_price_1m },
        { months: 3, price: car.sub_price_3m },
        { months: 6, price: car.sub_price_6m },
        { months: 12, price: car.sub_price_12m },
      ].filter((o) => o.price && o.price > 0)
    : [];
  const selectedSub = subOptions.find((o) => String(o.months) === String(form.binding_months));
  const monthlyTotal = selectedSub ? selectedSub.price + deductibleSel.addon : null;

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Udfyld venligst navn, email og telefon");
      return;
    }
    if (isRental && !form.start_date) {
      toast.error("Vælg ønsket startdato");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/bookings`, {
        car_plate: car.license_plate,
        type,
        ...form,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error("Noget gik galt — prøv igen om et øjeblik");
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-[#E5DED1] text-center" data-testid="booking-success">
        <div className="font-heading text-2xl text-graphite mb-2">Tak!</div>
        <p className="text-graphite/70">Vi vender tilbage snarest. Tjek gerne din mail for bekræftelse.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-graphite rounded-2xl p-6 text-white" data-testid="booking-form">
      <div className="label-tag text-copper mb-4">{isRental ? "Book denne bil" : "Spørg om denne bil"}</div>
      <div className="grid grid-cols-1 gap-3">
        <input className="public-input bg-graphite-light !text-white !border-white/15 placeholder-white/40" placeholder="Navn" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="booking-name"/>
        <div className="grid grid-cols-2 gap-3">
          <input className="public-input bg-graphite-light !text-white !border-white/15 placeholder-white/40" placeholder="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email"/>
          <input className="public-input bg-graphite-light !text-white !border-white/15 placeholder-white/40" placeholder="Telefon" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone"/>
        </div>
        {isRental && (
          <>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/60 mb-1 block flex items-center gap-1"><Calendar size={11}/>Ønsket startdato</span>
              <input className="public-input bg-graphite-light !text-white !border-white/15" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="booking-start"/>
            </label>
            {subOptions.length > 0 && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/60 mb-1 block">Bindingsperiode</span>
                <select
                  className="public-input bg-graphite-light !text-white !border-white/15"
                  value={form.binding_months}
                  onChange={(e) => set("binding_months", e.target.value)}
                  data-testid="booking-binding"
                >
                  <option value="">Vælg binding…</option>
                  {subOptions.map((o) => (
                    <option key={o.months} value={o.months}>
                      {o.months} {o.months === 1 ? "måned" : "måneder"} · {dkk(o.price)}/md
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div>
              <span className="text-[10px] uppercase tracking-wider text-white/60 mb-2 block">Selvrisiko</span>
              <div className="grid grid-cols-3 gap-2" data-testid="deductible-grid">
                {DEDUCTIBLE_OPTS.map((d) => {
                  const active = form.deductible === d.value;
                  return (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => set("deductible", d.value)}
                      className={`text-left rounded-xl border-2 px-3 py-2.5 transition-all ${
                        active ? "border-copper bg-copper/10" : "border-white/15 bg-graphite-light hover:border-white/30"
                      }`}
                      data-testid={`deductible-${d.value}`}
                    >
                      <div className="font-heading text-base text-white leading-tight">{d.label}</div>
                      <div className={`text-[10px] mt-0.5 ${active ? "text-copper" : "text-white/55"}`}>
                        {d.addon === 0 ? "Inkluderet" : `+${d.addon} kr./md`}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-white/45 mt-1.5">{deductibleSel.hint}</p>
            </div>
          </>
        )}
        <textarea className="public-input bg-graphite-light !text-white !border-white/15 placeholder-white/40 resize-none" placeholder="Besked (valgfrit)" rows="3" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="booking-message"/>

        {selectedSub && (
          <div className="border-t border-white/10 pt-3 mt-1 space-y-1" data-testid="booking-total">
            <div className="flex items-center justify-between text-white/70 text-xs">
              <span>Abonnement · {selectedSub.months} {selectedSub.months === 1 ? "md." : "mdr."}</span>
              <span>{dkk(selectedSub.price)}/md</span>
            </div>
            {deductibleSel.addon > 0 && (
              <div className="flex items-center justify-between text-white/70 text-xs">
                <span>Reduceret selvrisiko ({deductibleSel.label})</span>
                <span>+{dkk(deductibleSel.addon)}/md</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1.5 border-t border-white/10">
              <span className="text-white/70 text-sm">Pris pr. måned</span>
              <span className="font-heading text-2xl text-white">{dkk(monthlyTotal)}</span>
            </div>
            <div className="text-[10px] text-white/40 text-right">
              I alt over {selectedSub.months} {selectedSub.months === 1 ? "md." : "mdr."}: {dkk(monthlyTotal * selectedSub.months)}
            </div>
          </div>
        )}

        <button type="submit" disabled={busy} className="btn-secondary justify-center w-full mt-2" data-testid="booking-submit">
          {busy ? "Sender…" : (isRental ? "Send booking-forespørgsel" : "Send forespørgsel")}
        </button>
        <p className="text-white/50 text-xs text-center mt-1">Vi vender tilbage indenfor 24 timer.</p>
      </div>
    </form>
  );
}
